import React, { useEffect } from 'react';
import { ComposedModal, ModalBody } from 'carbon-components-react';
import CloseButton from '../close-button/CloseButton';
import { Icon } from '../../facades';
import { Name } from '../../facades/Icon';

export interface Modal {
  className?: string;
  open?: boolean;
  headerAdornmentIcon?: string;
  headerAdornmentIconColor?: string;
  header: string;
  children?: React.ReactNode;
  hasScrollingContent?: boolean;
  footerContent?: React.ReactNode;
  onClose: () => void;
  mobileFullScreen?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  ref?: React.MutableRefObject<any>;

}

/**
 * `Modal` is a component that displays a dialog window that sits on top of the page content.
 * For the body and footer, you are able to pass React nodes to give full customization of your modal design.
 * It requires a `header` and a `onClose` function that deals with closing the modal window. The modal's ability to open/close is
 * all handled in a single prop called `open` which takes in a boolean value.
 * The modal handles the close function in a couple of ways. You can either pass a component in the body/footer that handles the `onClose` function,
 * click outside of the modal component, or triggering the close with an `ESC` key.
 *
 * `headerAdornmentIcon` takes fontAwesome classes.
 *
 * To import the component use the import statement: `import { Modal } from '@asuresoftware/asure.design-system'`
 */

export const Modal = (props: Modal) => {
  const icon = props.headerAdornmentIcon as Name;
  const mobileFullScreen = props.mobileFullScreen ? ' mobile-full-screen' : '';
  const propsClassName = props.className ? ` ${props.className}` : '';

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        props.onClose();
      }
    }

    document.addEventListener('keyup', handleEscape);
    return () => {
      document.removeEventListener('keyup', handleEscape);
    };
  }, [props]);

  return (
    <ComposedModal data-testid="modal" ref={props.ref} open={props.open} onClose={props.onClose} className={`ac-modal${mobileFullScreen}${propsClassName}`} size={props.size}>
      <header className="d-flex justify-content-between">
        <div className="d-flex">
          {props.headerAdornmentIcon && <Icon color={props.headerAdornmentIconColor} className="ac-modal-header-adornment" icon={icon} lib="fal" />}
          <h1>{props.header}</h1>
        </div>
        <CloseButton textColor="text-dark" className="ac-modal-close-button ml-3" size="md" onClick={props.onClose} />
      </header>
      <ModalBody data-testid="modal-detail" hasScrollingContent={props.hasScrollingContent} aria-label={props.hasScrollingContent && 'modal-body'} >{props.children}</ModalBody>
      {props.footerContent && <footer>{props.footerContent}</footer>}
    </ComposedModal>
  );
};

export default Modal;
